import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SSE } from 'sse.js';

import {
  UPLOAD_PROGRESS_STATE_MESSAGE,
  type UploadExtractionSnapshot,
  type UploadProgressSnapshot,
} from '../../../backend/src/storage/upload-processing-events';
import { TERMINAL_UPLOAD_STATUSES, UploadProcessingStatus } from '../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig from '@/api/useGetFetchConfig';

export interface ExtractionProgress extends UploadExtractionSnapshot {
  /**
   * When this client first saw the extraction finish.
   */
  completedAt?: number;
}

export interface UploadProgressState extends Omit<UploadProgressSnapshot, 'status' | 'extractions'> {
  status: 'idle' | UploadProcessingStatus;
  extractions: ExtractionProgress[];
}

export const idleUploadProgress: UploadProgressState = {
  // Below any real snapshot, so the first one always applies.
  version: -1,
  status: 'idle',
  extractions: [],
  totalFiles: 0,
  hashedFiles: 0,
  convertedFiles: 0,
  duplicateCount: 0,
  deduplicateDocuments: true,
  successfullyUploadedFileCount: 0,
  unpackingSkippedCount: 0,
  textFallbackCount: 0,
  failuresByReason: {},
  downloadedFiles: 0,
  totalDownloadFiles: 0,
  preparingFetchedCount: 0,
  preparingTotalCount: 0,
};

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 500;

/**
 * Adopt a server-computed snapshot. The client stores what it is told and derives nothing, so a
 * message that is duplicated, re-delivered or missed entirely cannot put a count out of step.
 */
function applySnapshot(prev: UploadProgressState, snapshot: UploadProgressSnapshot): UploadProgressState {
  // A finished upload is a one-way door, so its snapshot is always worth taking: dropping it as
  // stale could only strand the view mid-upload.
  if (snapshot.version < prev.version && !TERMINAL_UPLOAD_STATUSES.has(snapshot.status)) {
    return prev;
  }

  const now = Date.now();
  return {
    ...snapshot,
    extractions: snapshot.extractions.map((extraction) => {
      const previous = prev.extractions.find((e) => e.extractionId === extraction.extractionId);
      if (!extraction.isComplete) return { ...extraction, completedAt: undefined };
      // Stamped only on the transition we witnessed: one already finished when we connected (a page
      // refresh mid-upload) must not reappear as freshly done.
      const justCompleted = previous !== undefined && !previous.isComplete;
      return { ...extraction, completedAt: justCompleted ? now : previous?.completedAt };
    }),
  };
}

/**
 * Upload progress for any number of concurrent uploads over a SINGLE SSE connection. Each message
 * is a whole snapshot for one upload, which is also exactly what the REST progress endpoint returns.
 */
export const useMultiUploadProgress = (matterId: string, uploadIds: string[]) => {
  const [stateMap, setStateMap] = useState<Map<string, UploadProgressState>>(new Map());
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const sseRef = useRef<SSE | null>(null);
  const retryCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { fetchConfigGET } = useGetFetchConfig();

  // Stable sorted key for dependency tracking
  const uploadIdsKey = useMemo(() => [...uploadIds].sort().join(','), [uploadIds]);

  const closeSSE = useCallback((shouldReconnect: boolean = true) => {
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    if (shouldReconnect && retryCountRef.current < MAX_RETRIES) {
      const delay = BASE_DELAY_MS * Math.pow(2, retryCountRef.current);
      retryCountRef.current += 1;

      reconnectTimeoutRef.current = setTimeout(() => {
        setReconnectTrigger((prev) => prev + 1);
      }, delay);
    }
  }, []);

  // Reset retry count only when the set of upload IDs changes — not on every reconnect,
  // otherwise the backoff stays at the first delay and MAX_RETRIES is never hit.
  useEffect(() => {
    retryCountRef.current = 0;
  }, [uploadIdsKey]);

  useEffect(() => {
    if (uploadIds.length === 0) return;

    const setupSSE = () => {
      try {
        const sseUrl = `${APIBaseChronos}/api/storage/upload-progress-multi?matterId=${encodeURIComponent(matterId)}&uploadIds=${encodeURIComponent(uploadIds.join(','))}`;

        const eventSource = new SSE(sseUrl, {
          headers: Object.fromEntries(fetchConfigGET.headers.entries()),
        });

        eventSource.onmessage = (event: any) => {
          retryCountRef.current = 0;

          try {
            if (!event.data || !event.data.trim()) return;

            const data = JSON.parse(event.data);
            // Anything else on the stream is a control frame (ping, connected).
            if (data.type !== UPLOAD_PROGRESS_STATE_MESSAGE) return;

            const uploadId = data.uploadId as string | undefined;
            if (!uploadId) return;

            setStateMap((prev) => {
              const next = new Map(prev);
              next.set(
                uploadId,
                applySnapshot(prev.get(uploadId) ?? idleUploadProgress, data as UploadProgressSnapshot),
              );
              return next;
            });
          } catch (error) {
            console.error('Error parsing upload progress SSE message:', error);
          }
        };

        eventSource.onerror = () => {
          closeSSE();
        };

        sseRef.current = eventSource;
      } catch (error) {
        console.error('Error setting up upload progress SSE:', error);
        closeSSE();
      }
    };

    setupSSE();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [matterId, uploadIdsKey, reconnectTrigger, fetchConfigGET, closeSSE]);

  return { stateMap };
};
